import { useCallback, useEffect, useMemo, useRef } from 'react';
// Imported from its module, not the `hooks` barrel: this hook runs inside
// `AudioProvider`, which wraps the whole app, so it must not depend on the barrel
// resolving. Suites that `jest.mock` the barrel with only the hooks they need would
// otherwise crash every render under AppProviders.
import usePageVisibility from 'students/views/shared/hooks/usePageVisibility';
import { resumeHowlerContext, TaskAudioManager } from '../classes';
import {
  IAudioManagerReturnedObject,
  ITaskAudioManagerItem,
  ITaskAudioRepositoryCollectionItem,
  tTaskAudioRepositoryCollection
} from '../models';

const useAudioManager = (): IAudioManagerReturnedObject => {
  const managerRef = useRef<TaskAudioManager>(new TaskAudioManager([]));

  const add = useCallback((item: ITaskAudioManagerItem): void => {
    managerRef.current.add(item);
  }, []);

  const addUnique = useCallback((item: ITaskAudioManagerItem): void => {
    managerRef.current.addUnique(item);
  }, []);

  const reset = useCallback((): void => {
    managerRef.current.reset();
  }, []);

  const unregister = useCallback((id: string | number): void => {
    managerRef.current.unregister(id);
  }, []);

  const unregisterUncached = useCallback((id: string | number): void => {
    managerRef.current.unregisterUncached(id);
  }, []);

  const unsubscribe = useCallback((): void => {
    managerRef.current.unsubscribe();
  }, []);

  const unload = useCallback((): void => {
    managerRef.current.unload();
  }, []);

  const stop = useCallback((): void => {
    managerRef.current.stop();
  }, []);

  const visibilityPause = useCallback((): void => {
    managerRef.current.visibilityPause();
  }, []);

  const visibilityPlay = useCallback((): void => {
    managerRef.current.visibilityPlay();
  }, []);

  const playById = useCallback((id: string | number): void => {
    return managerRef.current.playById(id);
  }, []);

  const getById = useCallback(
    (id: string | number): ITaskAudioRepositoryCollectionItem | null => {
      return managerRef.current.getById(id);
    },
    []
  );

  const getByTag = useCallback((tag: string): tTaskAudioRepositoryCollection => {
    return managerRef.current.getByTag(tag);
  }, []);

  const getAll = useCallback((): tTaskAudioRepositoryCollection => {
    return managerRef.current.getAll();
  }, []);

  const addTagToMute = useCallback((tag: string): void => {
    managerRef.current.addTagToMute(tag);
  }, []);

  const removeTagFromMute = useCallback((tag: string): void => {
    managerRef.current.removeTagFromMute(tag);
  }, []);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  /**
   * Exactly ONE visibility listener may drive the manager, so it lives next to
   * the instance it drives. It used to sit in `useTaskAudioPlayer`, once per
   * mounted consumer (WordNodes mounts one per NodePoint) — and since
   * `visibilityPause`/`visibilityPlay` act on the whole repository, each extra
   * listener started another Sound on every 'visible-paused' player.
   */
  const onVisibilityChange = useCallback(
    (isHidden: boolean) => {
      if (isHidden) {
        visibilityPause();
      } else {
        // Repair the shared WebAudio context first: the OS can leave it suspended
        // across a background spell, and any play into it is then silent.
        resumeHowlerContext();

        visibilityPlay();
      }
    },
    [visibilityPause, visibilityPlay]
  );

  usePageVisibility(onVisibilityChange);

  // The returned object must keep a stable identity: consumers use it as an
  // effect dependency / context value, and a fresh object per render re-runs
  // their cleanups — unloading every registered Howl on an unrelated re-render.
  return useMemo(
    () => ({
      add,
      addUnique,
      reset,
      unregister,
      unregisterUncached,
      unsubscribe,
      unload,
      stop,
      visibilityPause,
      visibilityPlay,
      playById,
      getById,
      getByTag,
      getAll,
      addTagToMute,
      removeTagFromMute,
      manager: managerRef.current
    }),
    [
      add,
      addUnique,
      reset,
      unregister,
      unregisterUncached,
      unsubscribe,
      unload,
      stop,
      visibilityPause,
      visibilityPlay,
      playById,
      getById,
      getByTag,
      getAll,
      addTagToMute,
      removeTagFromMute
    ]
  );
};

export default useAudioManager;
